import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  MenuItem,
  Popover,
  List,
  ListItemText,
  IconButton,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  CircularProgress,
  Chip,
} from "@mui/material";
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
  nodeTypeOptions,
  sentenceCaseWord,
  getLayoutedElements,
} from "./workflowUtils.ts";
import {
  CronScheduleBuilder,
  validateCronExpression,
} from "./CronScheduleBuilder.tsx";
import {
  WebhookConfigBuilder,
  validateWebhookConfig,
} from "./WebhookConfigBuilder.tsx";
import { getAgent, getTool } from "../../api/agentApi.js";
import { Node, Edge } from "reactflow";
import { StepType } from './WorkflowTypes';

interface AddNodePopoverProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  selectedType: StepType | "trigger" | null;
  onTypeSelect: (type: string) => void;
  onBackClick: () => void;
  formData: {
    id: string;
    version: string;
    prompt: string;
    triggerType: "schedule" | "webhook";
    cron: string;
    webhookConfig: {
      adapter_type: string;
      secrets: Record<string, string>;
    };
    input_mapping?: Record<string, string>;
    tool_parameters?: any[];
    timeout_seconds?: number;
  };
  onFormDataChange: (updates: Partial<AddNodePopoverProps["formData"]>) => void;
  agents: any[];
  tools: any[];
  onAddNode: () => void;
  nodes?: Node[];
  edges?: Edge[];
  setNodes?: React.Dispatch<React.SetStateAction<Node[]>>;
  setEdges?: React.Dispatch<React.SetStateAction<Edge[]>>;
}

const itemStyle = {
  borderRadius: "6px",
  mx: 1,
  mb: 0,
  py: 0.5,
  px: 1.5,
  transition: "all 0.1s ease",
  "&:hover": {
    bgcolor: "#F1F5F9",
    "& .MuiListItemText-primary": { color: "#0F172A" },
  },
};

const iconWrapperStyle = (color: string) => ({
  width: 32,
  height: 32,
  borderRadius: "6px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  mr: 1.5,
  bgcolor: color,
  color: "#FFFFFF",
  flexShrink: 0,
  "& svg": { fontSize: 18 },
});

export const AddNodePopover: React.FC<AddNodePopoverProps> = ({
  anchorEl,
  open,
  onClose,
  selectedType,
  onTypeSelect,
  onBackClick,
  formData,
  onFormDataChange,
  agents,
  tools,
  onAddNode,
  nodes,
  edges,
  setNodes,
  setEdges
}) => {
  const [search, setSearch] = useState("");
  const [showValidation, setShowValidation] = useState(false);
  const [loadingVersion, setLoadingVersion] = useState(false);

  // Reset validation and search when closing or changing type
  React.useEffect(() => {
    if (!open) {
      setShowValidation(false);
      setSearch("");
    }
  }, [open]);

  // Fetch and populate version when agent/tool is selected
  useEffect(() => {
    const fetchVersion = async () => {
      if (!formData.id || selectedType === "trigger") {
        return;
      }

      setLoadingVersion(true);
      try {
        let data;
        if (selectedType === "agent") {
          data = await getAgent(formData.id);
        } else if (selectedType === "tool") {
          data = await getTool(formData.id);
        }

        if (data && data.version !== undefined) {
          onFormDataChange({ version: String(data.version) });

          // Pre-populate input mapping for tool with parameter names
          if (selectedType === "tool" && data.parameters && Array.isArray(data.parameters)) {
            const prePopulatedMapping: Record<string, string> = {};
            data.parameters.forEach((param: any) => {
              if (param.name) {
                prePopulatedMapping[param.name] = ''; // Empty value, user fills it in
              }
            });
            // Also store the tool_parameters for reference (used for read-only logic)
            onFormDataChange({
              input_mapping: prePopulatedMapping,
              tool_parameters: data.parameters
            });
          }
        }
      } catch (error) {
        console.error(`Failed to fetch ${selectedType} version:`, error);
      } finally {
        setLoadingVersion(false);
      }
    };

    fetchVersion();
  }, [formData.id, selectedType]);

  const handleCancel = () => {
    setShowValidation(false);
    setSearch("");
    onClose(); // This will reset formData in parent
  };

  // Helper to check if a trigger type already exists in the workflow
  const hasTriggerType = (triggerType: "schedule" | "webhook"): boolean => {
    if (!nodes) return false;
    return nodes.some(
      (node) =>
        node.data.type === "trigger" &&
        node.data.config.triggerType === triggerType
    );
  };

  const handleAddClick = () => {
    // Check if trigger type already exists
    if (selectedType === "trigger") {
      if (hasTriggerType(formData.triggerType)) {
        alert(
          `A ${formData.triggerType} trigger already exists in this workflow. Only one ${formData.triggerType} trigger is allowed per workflow.`
        );
        return;
      }
    }

    // For schedule triggers, validate cron before adding
    if (selectedType === "trigger" && formData.triggerType === "schedule") {
      const validation = validateCronExpression(formData.cron);
      if (!validation.valid) {
        setShowValidation(true);
        return; // Don't add if validation fails
      }
    }

    // For webhook triggers, validate webhook config before adding
    if (selectedType === "trigger" && formData.triggerType === "webhook") {
      const validation = validateWebhookConfig(formData.webhookConfig);
      if (!validation.valid) {
        setShowValidation(true);
        return; // Don't add if validation fails
      }
    }

    setShowValidation(false);
    onAddNode();
  };

  const onLayout = () => {
    const { nodes: layoutedNodes, edges: layoutedEdges } = getLayoutedElements(
      nodes ?? [],
      edges ?? []
    );
    if (setNodes) {
      setNodes([...layoutedNodes]);
    }
    if (setEdges) {
      setEdges([...layoutedEdges]);
    }
    onClose();
    // Smoothly zoom to show the new layout
    // window.requestAnimationFrame(() => fitView({ duration: 800 }));
  };

  const AddNodeText = selectedType === "trigger" ? "Add Trigger" : "Add Step";

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
      slotProps={{
        paper: {
          sx: {
            width: 280,
            borderRadius: "10px",
            boxShadow:
              "0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -2px rgba(0,0,0,0.05)",
            border: "1px solid #E2E8F0",
            bgcolor: "#FFFFFF",
            WebkitFontSmoothing: "antialiased",
          },
        },
      }}
    >
      {/* 1. HEADER */}
      <Box
        sx={{
          px: 1.5,
          py: 1,
          display: "flex",
          alignItems: "center",
          borderBottom: "1px solid #F1F5F9",
          bgcolor: "#F8FAFC",
        }}
      >
        {selectedType && (
          <IconButton size="small" onClick={onBackClick} sx={{ mr: 1, p: 0.5 }}>
            <ArrowBackIcon sx={{ fontSize: 14 }} />
          </IconButton>
        )}
        <Typography variant="body2" fontWeight="700" color="#1E293B">
          {selectedType
            ? `Configure ${
                selectedType === "trigger" ? formData.triggerType : selectedType
              }`
            : "Add Component"}
        </Typography>
      </Box>

      <Box sx={{ pb: 1 }}>
        {!selectedType ? (
          <>
            {/* COMPONENT LIST */}
            <List disablePadding sx={{ maxHeight: "72%", overflowY: "auto" }}>
              {nodeTypeOptions.map((group) => (
                <Box key={group.category} sx={{ mt: 1 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      px: 2.5,
                      fontWeight: 700,
                      color: "#94A3B8",
                      fontSize: "12px",
                      display: "block",
                      mb: 0.5,
                    }}
                  >
                    {group.category}
                  </Typography>
                  {group.options
                    .filter((opt) =>
                      opt.label.toLowerCase().includes(search.toLowerCase())
                    )
                    .map((option) => (
                      <MenuItem
                        key={option.id}
                        onClick={() => {
                          if (option.id === "auto-layout") {
                            onLayout();
                            return;
                          }
                          onTypeSelect(option.id);
                        }}
                        sx={itemStyle}
                      >
                        <Box sx={iconWrapperStyle(option.color)}>
                          <option.icon />
                        </Box>
                        <ListItemText
                          primary={option.label}
                          secondary={option.description}
                          primaryTypographyProps={{
                            fontSize: "13px",
                            fontWeight: 600,
                            color: "#334155",
                          }}
                          secondaryTypographyProps={{
                            fontSize: "11px",
                            color: "#94A3B8",
                          }}
                        />
                      </MenuItem>
                    ))}
                </Box>
              ))}
            </List>
          </>
        ) : (
          /* 4. CONFIG FORM */
          <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2 }}>
            {selectedType === "trigger" ? (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 800, color: "#64748B", fontSize: "11px" }}
                >
                  {sentenceCaseWord(formData.triggerType)} Settings
                </Typography>
                {formData.triggerType === "schedule" ? (
                  <CronScheduleBuilder
                    value={formData.cron}
                    onChange={(cron) => onFormDataChange({ cron })}
                    showValidation={showValidation}
                  />
                ) : (
                  <WebhookConfigBuilder
                    value={formData.webhookConfig}
                    onChange={(webhookConfig) =>
                      onFormDataChange({ webhookConfig })
                    }
                    showValidation={showValidation}
                  />
                )}
              </Box>
            ) : selectedType === "agent" || selectedType === "tool" ? (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                {/* SELECTOR */}
                <FormControl fullWidth size="small">
                  <InputLabel sx={{ fontSize: "11px", fontWeight: 700 }}>
                    Select {sentenceCaseWord(selectedType)}
                  </InputLabel>
                  <Select
                    value={formData.id}
                    label={`SELECT ${selectedType.toUpperCase()}`}
                    onChange={(e) => onFormDataChange({ id: e.target.value })}
                    sx={{ fontSize: "12px" }}
                  >
                    {(selectedType === "agent" ? agents : tools).map(
                      (item: any) => (
                        <MenuItem
                          key={item.id || item.agent_id}
                          value={item.id || item.agent_id}
                          sx={{ fontSize: "12px" }}
                        >
                          {item.display_name || item.name}
                        </MenuItem>
                      )
                    )}
                  </Select>
                </FormControl>

                {/* PROMPT (AGENT ONLY) */}
                {selectedType === "agent" && (
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    label="Instructions (Prompt) - Optional"
                    placeholder="Add custom instructions or leave empty to use agent defaults..."
                    value={formData.prompt}
                    onChange={(e) =>
                      onFormDataChange({ prompt: e.target.value })
                    }
                    InputLabelProps={{
                      sx: { fontSize: "11px", fontWeight: 700 },
                    }}
                    inputProps={{ style: { fontSize: "12px" } }}
                    helperText="Can be configured later in the properties sidebar"
                    FormHelperTextProps={{ sx: { fontSize: "10px", mt: 0.5 } }}
                  />
                )}

                {/* VERSION (BELOW PROMPT) */}
                <TextField
                  fullWidth
                  size="small"
                  label="Version"
                  placeholder={loadingVersion ? "Loading..." : "e.g. 1"}
                  value={formData.version}
                  onChange={(e) =>
                    onFormDataChange({ version: e.target.value })
                  }
                  disabled={loadingVersion}
                  InputLabelProps={{
                    sx: { fontSize: "11px", fontWeight: 700 },
                  }}
                  inputProps={{ style: { fontSize: "12px" } }}
                  InputProps={{
                    endAdornment: loadingVersion ? (
                      <InputAdornment position="end">
                        <CircularProgress size={16} />
                      </InputAdornment>
                    ) : null,
                  }}
                  helperText={
                    loadingVersion ? "Fetching current version..." : ""
                  }
                  FormHelperTextProps={{ sx: { fontSize: "10px", mt: 0.5 } }}
                />

                {/* TIMEOUT (AGENT AND TOOL) */}
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label="Timeout (seconds)"
                  placeholder="e.g. 180"
                  value={formData.timeout_seconds || ''}
                  onChange={(e) =>
                    onFormDataChange({ timeout_seconds: parseInt(e.target.value) || undefined })
                  }
                  InputLabelProps={{
                    sx: { fontSize: "11px", fontWeight: 700 },
                  }}
                  inputProps={{ style: { fontSize: "12px" }, min: 1 }}
                  helperText="Maximum execution time for this step"
                  FormHelperTextProps={{ sx: { fontSize: "10px", mt: 0.5 } }}
                />
              </Box>
            ) : selectedType === "conditional" ? (
              /* CONDITIONAL CONFIG */
              <ConditionalConfigForm formData={formData} onFormDataChange={onFormDataChange} />
            ) : selectedType === "human_input" ? (
              /* HUMAN INPUT CONFIG */
              <HumanInputConfigForm formData={formData} onFormDataChange={onFormDataChange} />
            ) : selectedType === "loop" ? (
              /* LOOP CONFIG */
              <LoopConfigForm formData={formData} onFormDataChange={onFormDataChange} />
            ) : selectedType === "merge" ? (
              /* MERGE CONFIG */
              <MergeConfigForm formData={formData} onFormDataChange={onFormDataChange} />
            ) : selectedType === "wait" ? (
              /* WAIT CONFIG */
              <WaitConfigForm formData={formData} onFormDataChange={onFormDataChange} />
            ) : null}

            {/* ACTION BUTTONS */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 1,
                pt: 1,
                borderTop: "1px solid #F1F5F9",
              }}
            >
              <Button
                onClick={handleCancel}
                size="small"
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  fontSize: "11px",
                  color: "#64748B",
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleAddClick}
                size="small"
                disableElevation
                disabled={
                  // Disable if trigger type already exists
                  (selectedType === "trigger" && hasTriggerType(formData.triggerType)) ||
                  // Minimum required validation - only require IDs, everything else can be configured in sidebar
                  (selectedType === "agent"
                    ? !formData.id
                    : selectedType === "tool"
                    ? !formData.id
                    : selectedType === "trigger" &&
                      formData.triggerType === "schedule"
                    ? !validateCronExpression(formData.cron).valid
                    : selectedType === "trigger" &&
                      formData.triggerType === "webhook"
                    ? !validateWebhookConfig(formData.webhookConfig).valid
                    : selectedType === "human_input"
                    ? !(formData as any).title
                    : selectedType === "conditional"
                    ? !(formData as any).routing_config?.branches?.length
                    : selectedType === "wait"
                    ? !(formData as any).wait_config?.duration_seconds
                    : false)
                }
                // sx={{ width: "100%", maxWidth: 300 }}
              >
                {AddNodeText}
              </Button>
            </Box>
          </Box>
        )}
      </Box>
    </Popover>
  );
};

/* ─── SUB-COMPONENT: Conditional Config ─── */
const labelSx = { fontSize: '11px', fontWeight: 700 };
const inputSx = { style: { fontSize: '12px' } };

const ConditionalConfigForm: React.FC<{ formData: any; onFormDataChange: (u: any) => void }> = ({ formData, onFormDataChange }) => {
  const config = formData.routing_config || { type: 'expression', branches: [] };
  const updateConfig = (patch: any) => onFormDataChange({ routing_config: { ...config, ...patch } });
  const addBranch = () => updateConfig({ branches: [...(config.branches || []), { id: `branch_${Date.now()}`, condition: '' }] });
  const updateBranch = (i: number, key: string, val: any) => {
    const branches = [...(config.branches || [])];
    branches[i] = { ...branches[i], [key]: val };
    updateConfig({ branches });
  };
  const removeBranch = (i: number) => {
    const branches = [...(config.branches || [])];
    branches.splice(i, 1);
    updateConfig({ branches });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Typography variant="caption" sx={{ fontWeight: 800, color: '#64748B', fontSize: '11px' }}>
        Conditional Routing
      </Typography>
      <FormControl fullWidth size="small">
        <InputLabel sx={labelSx}>Routing Type</InputLabel>
        <Select value={config.type || 'expression'} label="Routing Type" onChange={(e) => updateConfig({ type: e.target.value })} sx={{ fontSize: '12px' }}>
          <MenuItem value="expression" sx={{ fontSize: '12px' }}>Expression</MenuItem>
          <MenuItem value="llm" sx={{ fontSize: '12px' }}>LLM-based</MenuItem>
        </Select>
      </FormControl>
      {config.type === 'llm' && (
        <TextField fullWidth size="small" label="LLM Prompt" value={config.llm_prompt || ''} onChange={(e) => updateConfig({ llm_prompt: e.target.value })} InputLabelProps={{ sx: labelSx }} inputProps={inputSx} multiline rows={2} />
      )}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="caption" sx={{ fontWeight: 700, color: '#475569', fontSize: '11px' }}>Branches</Typography>
        <IconButton size="small" onClick={addBranch}><AddCircleOutlineIcon sx={{ fontSize: 16 }} /></IconButton>
      </Box>
      {(config.branches || []).map((b: any, i: number) => (
        <Box key={i} sx={{ display: 'flex', gap: 0.5, alignItems: 'flex-start' }}>
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <TextField fullWidth size="small" label="Branch ID" value={b.id || ''} onChange={(e) => updateBranch(i, 'id', e.target.value)} InputLabelProps={{ sx: labelSx }} inputProps={inputSx} placeholder="e.g. approve" />
            <TextField fullWidth size="small" label="Condition" value={b.condition || ''} onChange={(e) => updateBranch(i, 'condition', e.target.value)} InputLabelProps={{ sx: labelSx }} inputProps={inputSx} placeholder={config.type === 'llm' ? 'Label name' : "e.g. status == 'approved'"} />
          </Box>
          <IconButton size="small" onClick={() => removeBranch(i)} sx={{ mt: 0.5 }}><DeleteOutlineIcon sx={{ fontSize: 14, color: '#EF4444' }} /></IconButton>
        </Box>
      ))}
      <Typography variant="caption" sx={{ fontSize: '10px', color: '#94A3B8', fontStyle: 'italic' }}>
        Branches are connected via edges in the workflow. Next steps will be inferred from the graph.
      </Typography>
    </Box>
  );
};

/* ─── SUB-COMPONENT: Human Input Config ─── */
const HumanInputConfigForm: React.FC<{ formData: any; onFormDataChange: (u: any) => void }> = ({ formData, onFormDataChange }) => {
  const fields = formData.input_fields || [];
  const addField = () => onFormDataChange({ input_fields: [...fields, { name: '', type: 'text', required: true }] });
  const updateField = (i: number, key: string, val: any) => {
    const updated = [...fields];
    updated[i] = { ...updated[i], [key]: val };
    onFormDataChange({ input_fields: updated });
  };
  const removeField = (i: number) => {
    const updated = [...fields];
    updated.splice(i, 1);
    onFormDataChange({ input_fields: updated });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Typography variant="caption" sx={{ fontWeight: 800, color: '#64748B', fontSize: '11px' }}>
        Human Approval Settings
      </Typography>
      <TextField fullWidth size="small" label="Title" value={formData.title || ''} onChange={(e) => onFormDataChange({ title: e.target.value })} InputLabelProps={{ sx: labelSx }} inputProps={inputSx} />
      <TextField fullWidth size="small" label="Prompt" value={formData.prompt || ''} onChange={(e) => onFormDataChange({ prompt: e.target.value })} InputLabelProps={{ sx: labelSx }} inputProps={inputSx} multiline rows={2} />
      <TextField fullWidth size="small" label="Timeout (minutes)" type="number" value={formData.timeout_minutes || ''} onChange={(e) => onFormDataChange({ timeout_minutes: parseInt(e.target.value) || undefined })} InputLabelProps={{ sx: labelSx }} inputProps={inputSx} />
      <FormControl fullWidth size="small">
        <InputLabel sx={labelSx}>On Timeout</InputLabel>
        <Select value={formData.on_timeout || 'fail'} label="On Timeout" onChange={(e) => onFormDataChange({ on_timeout: e.target.value })} sx={{ fontSize: '12px' }}>
          <MenuItem value="fail" sx={{ fontSize: '12px' }}>Fail workflow</MenuItem>
          <MenuItem value="skip" sx={{ fontSize: '12px' }}>Skip step</MenuItem>
        </Select>
      </FormControl>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="caption" sx={{ fontWeight: 700, color: '#475569', fontSize: '11px' }}>Data to Display</Typography>
        <IconButton size="small" onClick={() => {
          const dataToDisplay = formData.data_to_display || {};
          const newKey = `field_${Object.keys(dataToDisplay).length + 1}`;
          onFormDataChange({ data_to_display: { ...dataToDisplay, [newKey]: '' } });
        }}><AddCircleOutlineIcon sx={{ fontSize: 16 }} /></IconButton>
      </Box>
      <Typography variant="caption" sx={{ fontSize: '10px', color: '#94A3B8', mb: 0.5 }}>
        Define what data to show to the reviewer. Use JSONPath expressions.
      </Typography>
      {Object.entries(formData.data_to_display || {}).map(([key, value]: [string, any], i: number) => (
        <Box key={i} sx={{ display: 'flex', gap: 0.5, alignItems: 'flex-start' }}>
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <TextField fullWidth size="small" label="Display Field Name" value={key} onChange={(e) => {
              const dataToDisplay = formData.data_to_display || {};
              const newData = { ...dataToDisplay };
              delete newData[key];
              newData[e.target.value] = value;
              onFormDataChange({ data_to_display: newData });
            }} InputLabelProps={{ sx: labelSx }} inputProps={inputSx} placeholder="e.g. campaign_plan" />
            <TextField fullWidth size="small" label="JSONPath Expression" value={value} onChange={(e) => {
              onFormDataChange({ data_to_display: { ...(formData.data_to_display || {}), [key]: e.target.value } });
            }} InputLabelProps={{ sx: labelSx }} inputProps={inputSx} placeholder="$.steps.[step_id].output" />
          </Box>
          <IconButton size="small" onClick={() => {
            const dataToDisplay = { ...(formData.data_to_display || {}) };
            delete dataToDisplay[key];
            onFormDataChange({ data_to_display: dataToDisplay });
          }} sx={{ mt: 0.5 }}><DeleteOutlineIcon sx={{ fontSize: 14, color: '#EF4444' }} /></IconButton>
        </Box>
      ))}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1.5 }}>
        <Typography variant="caption" sx={{ fontWeight: 700, color: '#475569', fontSize: '11px' }}>Input Fields</Typography>
        <IconButton size="small" onClick={addField}><AddCircleOutlineIcon sx={{ fontSize: 16 }} /></IconButton>
      </Box>
      {fields.map((f: any, i: number) => (
        <Box key={i} sx={{ display: 'flex', gap: 0.5, alignItems: 'flex-start' }}>
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <TextField fullWidth size="small" label="Field Name" value={f.name || ''} onChange={(e) => updateField(i, 'name', e.target.value)} InputLabelProps={{ sx: labelSx }} inputProps={inputSx} />
            <TextField fullWidth size="small" label="Label" value={f.label || ''} onChange={(e) => updateField(i, 'label', e.target.value)} InputLabelProps={{ sx: labelSx }} inputProps={inputSx} placeholder="Display label (optional)" />
            <FormControl fullWidth size="small">
              <InputLabel sx={labelSx}>Type</InputLabel>
              <Select value={f.type || 'text'} label="Type" onChange={(e) => updateField(i, 'type', e.target.value)} sx={{ fontSize: '12px' }}>
                <MenuItem value="text" sx={{ fontSize: '12px' }}>Text</MenuItem>
                <MenuItem value="textarea" sx={{ fontSize: '12px' }}>Textarea</MenuItem>
                <MenuItem value="select" sx={{ fontSize: '12px' }}>Select</MenuItem>
                <MenuItem value="boolean" sx={{ fontSize: '12px' }}>Boolean</MenuItem>
              </Select>
            </FormControl>
            {f.type === 'select' && (
              <TextField
                fullWidth
                size="small"
                label="Options (comma-separated)"
                value={Array.isArray(f.options) ? f.options.join(', ') : String(f.options || '')}
                onChange={(e) => {
                  // Store the raw string - don't split yet
                  updateField(i, 'options', e.target.value);
                }}
                onBlur={(e) => {
                  // Only split into array when user finishes editing
                  const optionsArray = e.target.value.split(',').map((s: string) => s.trim()).filter(Boolean);
                  updateField(i, 'options', optionsArray);
                }}
                InputLabelProps={{ sx: labelSx }}
                inputProps={inputSx}
                placeholder="e.g. Approve, Revise, Reject"
                helperText="Enter comma-separated values"
                FormHelperTextProps={{ sx: { fontSize: '10px', mt: 0.5 } }}
              />
            )}
            <FormControl fullWidth size="small">
              <InputLabel sx={labelSx}>Required</InputLabel>
              <Select value={f.required !== undefined ? f.required : true} label="Required" onChange={(e) => updateField(i, 'required', e.target.value === 'true' || e.target.value === true)} sx={{ fontSize: '12px' }}>
                <MenuItem value={true} sx={{ fontSize: '12px' }}>Yes</MenuItem>
                <MenuItem value={false} sx={{ fontSize: '12px' }}>No</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <IconButton size="small" onClick={() => removeField(i)} sx={{ mt: 0.5 }}><DeleteOutlineIcon sx={{ fontSize: 14, color: '#EF4444' }} /></IconButton>
        </Box>
      ))}
    </Box>
  );
};

/* ─── SUB-COMPONENT: Loop Config ─── */
const LoopConfigForm: React.FC<{ formData: any; onFormDataChange: (u: any) => void }> = ({ formData, onFormDataChange }) => {
  const config = formData.loop_config || { type: 'for_each' };
  const updateConfig = (patch: any) => onFormDataChange({ loop_config: { ...config, ...patch } });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Typography variant="caption" sx={{ fontWeight: 800, color: '#64748B', fontSize: '11px' }}>
        Loop Configuration
      </Typography>
      <FormControl fullWidth size="small">
        <InputLabel sx={labelSx}>Loop Type</InputLabel>
        <Select value={config.type || 'for_each'} label="Loop Type" onChange={(e) => updateConfig({ type: e.target.value })} sx={{ fontSize: '12px' }}>
          <MenuItem value="for_each" sx={{ fontSize: '12px' }}>For Each</MenuItem>
          <MenuItem value="while" sx={{ fontSize: '12px' }}>While</MenuItem>
        </Select>
      </FormControl>
      {config.type === 'for_each' ? (
        <TextField fullWidth size="small" label="Collection (JSONPath)" value={config.collection || ''} onChange={(e) => updateConfig({ collection: e.target.value })} InputLabelProps={{ sx: labelSx }} inputProps={inputSx} placeholder="e.g. $.steps.fetch_data.output.items" />
      ) : (
        <TextField fullWidth size="small" label="While Condition" value={config.condition || ''} onChange={(e) => updateConfig({ condition: e.target.value })} InputLabelProps={{ sx: labelSx }} inputProps={inputSx} placeholder="e.g. retry_count < 3" />
      )}
      <TextField fullWidth size="small" label="Max Iterations" type="number" value={config.max_iterations || ''} onChange={(e) => updateConfig({ max_iterations: parseInt(e.target.value) || undefined })} InputLabelProps={{ sx: labelSx }} inputProps={inputSx} />
      <FormControl fullWidth size="small">
        <InputLabel sx={labelSx}>Output Mode</InputLabel>
        <Select value={formData.output_mode || 'collect'} label="Output Mode" onChange={(e) => onFormDataChange({ output_mode: e.target.value })} sx={{ fontSize: '12px' }}>
          <MenuItem value="collect" sx={{ fontSize: '12px' }}>Collect All</MenuItem>
          <MenuItem value="last" sx={{ fontSize: '12px' }}>Last Only</MenuItem>
        </Select>
      </FormControl>
    </Box>
  );
};

/* ─── SUB-COMPONENT: Merge Config ─── */
const MergeConfigForm: React.FC<{ formData: any; onFormDataChange: (u: any) => void }> = ({ formData, onFormDataChange }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
    <Typography variant="caption" sx={{ fontWeight: 800, color: '#64748B', fontSize: '11px' }}>
      Merge Configuration
    </Typography>
    <FormControl fullWidth size="small">
      <InputLabel sx={labelSx}>Merge Strategy</InputLabel>
      <Select value={formData.merge_strategy || 'all'} label="Merge Strategy" onChange={(e) => onFormDataChange({ merge_strategy: e.target.value })} sx={{ fontSize: '12px' }}>
        <MenuItem value="all" sx={{ fontSize: '12px' }}>All (combine all inputs)</MenuItem>
        <MenuItem value="all_available" sx={{ fontSize: '12px' }}>All Available</MenuItem>
        <MenuItem value="first_available" sx={{ fontSize: '12px' }}>First Available</MenuItem>
        <MenuItem value="array" sx={{ fontSize: '12px' }}>Array (list of outputs)</MenuItem>
      </Select>
    </FormControl>
    <Typography variant="caption" sx={{ color: '#94A3B8', fontSize: '10px' }}>
      Merge steps combine outputs from parallel branches. Dependencies determine which branches are merged.
    </Typography>
  </Box>
);

/* ─── SUB-COMPONENT: Wait Config ─── */
const WaitConfigForm: React.FC<{ formData: any; onFormDataChange: (u: any) => void }> = ({ formData, onFormDataChange }) => {
  const config = formData.wait_config || { type: 'duration', duration_seconds: 30 };
  const updateConfig = (patch: any) => onFormDataChange({ wait_config: { ...config, ...patch } });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Typography variant="caption" sx={{ fontWeight: 800, color: '#64748B', fontSize: '11px' }}>
        Wait Configuration
      </Typography>
      <TextField fullWidth size="small" label="Duration (seconds)" type="number" value={config.duration_seconds || ''} onChange={(e) => updateConfig({ type: 'duration', duration_seconds: parseInt(e.target.value) || 0 })} InputLabelProps={{ sx: labelSx }} inputProps={inputSx} helperText="≤60s runs inline; >60s checkpoints and resumes via Celery Beat" FormHelperTextProps={{ sx: { fontSize: '10px' } }} />
    </Box>
  );
};
